import { Box, Grid, Tooltip, Typography } from "@mui/material";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ReTooltip,
  Legend,
} from "recharts";
import React, { useMemo } from "react";
import { useAccess } from "./access/AccessContext";
import { PARTY_COLOURS as _PARTY_COLOURS } from "./constants/partyConfig";

const PARTY_COLOURS = { ..._PARTY_COLOURS, coalition: "#13265C" };

const ISSUE_COLOURS = {
  inflation: "#a9a19e",
  health: "#e3779c",
  economy: "#88ccee",
  crime: "#9467bd",
  housing: "#c49c94",
  poverty: "#d62728",
  unemployment: "#b59f3b",
  climate: "#2ca02c",
  education: "#1f77b4",
  transport: "#ff7f0e",
  drugs: "#bcbd22",
  race_relations: "#17becf",
  tax: "#ff9896",
  pollution: "#7f7f7f",
  maori: "#8c564b",
  immigration: "#bcbd22",
  defence: "#1f77b4",
};

const ISSUE_NAMES = {
  unemployment: "Unemployment",
  inflation: "Inflation",
  economy: "Economy",
  health: "Health",
  housing: "Housing",
  crime: "Crime",
  climate: "Climate",
  econ: "Economy",
  drugs: "Drugs",
  defence: "Defence",
  education: "Education",
  maori: "Maori",
  pollution: "Pollution",
  poverty: "Poverty",
  race_rel: "Race relations",
  tax: "Tax",
  transport: "Transport",
};

const PARTY_NAME = {
  act: "ACT",
  green: "Green",
  lab: "Labour",
  nat: "National",
  nzf: "NZF",
  tpm: "TPM",
  top: "TOP",
  coalition: "Coalition",
  oth: "Other",
};

const NICE_NAME = {
  F: "Female",
  M: "Male",
  Young: "Young",
  Middle: "Middle",
  Old: "Old",
  "Asian only": "Asian only",
  "European only": "European only",
  "European/Māori": "European/Māori",
  "Māori only": "Māori only",
  Other: "Other",
  "Pacific Peoples only": "Pacific Peoples only",
  ACT: "ACT 2023",
  GREEN: "Green 2023",
  LAB: "Labour 2023",
  NAT: "National 2023",
  NZF: "NZF 2023",
  TPM: "TPM 2023",
  OTH: "Other 2023",
  DNV: "Did not vote",
};

// Every series the vote line chart can draw. Party users see only their own —
// "coalition" is a bloc aggregate, so it stays internal.
export const ALL_PARTY_KEYS = ["nat", "lab", "act", "green", "tpm","top",  "nzf", "coalition", "oth"];

/**
 * The series this user's vote line chart will draw. Exported so the legend in
 * VotersDemographics is built from the same list as the lines themselves.
 */
export const voteLinePartyKeys = (access) =>
  access?.is_internal
    ? ALL_PARTY_KEYS
    : ALL_PARTY_KEYS.filter((key) => access?.visible_parties?.includes(key));

export default function DemogVoteAndIssues({ scatterDemogsRows, chosenDemog }) {
  const { access } = useAccess();
  const partyKeys = useMemo(() => voteLinePartyKeys(access), [access]);
  const rows = scatterDemogsRows?.filter((r) => r.type === chosenDemog) || [];

  const niceOrder = Object.keys(NICE_NAME);
  const sortedRows = [...rows].sort(
    (a, b) => niceOrder.indexOf(a.type_code) - niceOrder.indexOf(b.type_code)
  );

  const waves = useMemo(() => {
    const waveSet = new Map();
    for (const r of rows) {
      const w = Number(r.wave);
      if (!waveSet.has(w)) waveSet.set(w, r.wave_date);
    }
    return Array.from(waveSet.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([wave, date]) => ({ wave, date }));
  }, [rows]);

  const latestWave = waves.length ? waves[waves.length - 1].wave : null;
  const prevWave = waves.length > 1 ? waves[waves.length - 2].wave : null;

  const demogOrder = useMemo(() => {
    const latestRows = sortedRows.filter((r) => Number(r.wave) === latestWave);
    return latestRows.map((r) => NICE_NAME[r.type_name]).filter(Boolean);
  }, [sortedRows, latestWave]);

  const lineChartDataByDemog = useMemo(() => {
    const result = {};
    for (const demog of demogOrder) {
      result[demog] = waves.map(({ wave, date }) => {
        const row = sortedRows.find(
          (r) => Number(r.wave) === wave && NICE_NAME[r.type_name] === demog
        );
        const point = { date, wave };
        if (row) {
          for (const party of partyKeys) {
            if (party === "coalition") {
              point[party] =
                ((row.nat_vi || 0) + (row.act_vi || 0) + (row.nzf_vi || 0)) * 100;
            } else {
              point[party] = (row[`${party}_vi`] || 0) * 100;
            }
          }
        }
        return point;
      });
    }
    return result;
  }, [sortedRows, waves, demogOrder, partyKeys]);

  const issueDataByDemog = useMemo(() => {
    if (!scatterDemogsRows?.length) return {};
    const issueKeys = Object.keys(scatterDemogsRows[0]).filter((k) =>
      k.endsWith("_issue_n")
    );
    const result = {};
    for (const demog of demogOrder) {
      const latestRow = sortedRows.find(
        (r) => Number(r.wave) === latestWave && NICE_NAME[r.type_name] === demog
      );
      const prevRow = prevWave
        ? sortedRows.find(
            (r) => Number(r.wave) === prevWave && NICE_NAME[r.type_name] === demog
          )
        : null;
      if (!latestRow) {
        result[demog] = [];
        continue;
      }
      result[demog] = issueKeys
        .map((k) => ({ key: k.replace("_issue_n", ""), val: latestRow[k] }))
        .sort((a, b) => b.val - a.val)
        .slice(0, 3)
        .map((iss) => {
          const prevVal = prevRow ? prevRow[`${iss.key}_issue_n`] : null;
          const diff = prevVal != null && iss.val != null ? iss.val - prevVal : null;
          return { ...iss, diff };
        });
    }
    return result;
  }, [scatterDemogsRows, sortedRows, demogOrder, latestWave, prevWave]);

  if (!demogOrder.length) return null;

  const formatDate = (d) => {
    if (!d) return "";
    // wave_date changed from "September 2025" to "4th October 2026" at wave 4, and
    // Date cannot parse the ordinal suffix — new Date("4th October 2026") is Invalid
    // Date, which rendered as "Invalid Date" on the axis. Strip the suffix first;
    // "Month Year" is unaffected. Anything still unparseable shows as given rather
    // than as "Invalid Date".
    const date = new Date(String(d).replace(/(\d+)(st|nd|rd|th)\b/gi, "$1"));
    if (Number.isNaN(date.getTime())) return String(d);
    return date.toLocaleDateString("en-NZ", { month: "long", year: "numeric" });
  };

  return (
    <Box sx={{ height: "100%", width: "100vw", display: "flex", flexDirection: "column", gap: 0.5, overflow: "auto", pb: 16 }}>
      {/* Header row */}

      {/* One row per demographic — chart + issues locked together */}
      {demogOrder.map((demog) => {
        const issues = issueDataByDemog[demog] || [];
        return (
          <Box
            key={demog}
            sx={{
              display: "flex",
              flex: 1,
              minHeight: 140,
              width: "100%",
            }}
          >
            {/* Left: line chart */}
            <Box sx={{ width: "50%", pr: 0.5,
    position: "relative",
    "&:hover": { zIndex: 10 }, }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ pl: 1, mb: -0.5 }}>
                {demog}
              </Typography>
              <ResponsiveContainer width="100%" height="85%">
                <LineChart data={lineChartDataByDemog[demog] || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tickFormatter={formatDate} fontSize={11} />
                  <YAxis
                    domain={[0, 90]}
                    tickFormatter={(v) => `${v}%`}
                    fontSize={11}
                    width={40}
                  />
                  <ReTooltip
  formatter={(val, name) => [`${val.toFixed(1)}%`, PARTY_NAME[name]]}
  labelFormatter={formatDate}
  allowEscapeViewBox={{ x: false, y: false }}
  wrapperStyle={{ zIndex: 20 }}
  itemSorter={(item) => -item.value}
/>
                  {partyKeys.map((party) => (
                    <Line
                      key={party}
                      type="monotone"
                      dataKey={party}
                      stroke={PARTY_COLOURS[party]}
                      strokeWidth={party === "coalition" ? 3 : 2}
                      strokeDasharray={party === "coalition" ? "6 3" : undefined}
                      dot={{ r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </Box>

            {/* Right: issue boxes */}
            <Box
              sx={{
                width: "50%",
                display: "flex",
                gap: 0.5,
                pl: 0.5,
                alignItems: "stretch",
              }}
            >
              {issues.map((iss) => {
                let arrow = "";
                let arrowColor = "white";
                if (iss.diff !== null) {
                  if (iss.diff > 0.005) {
                    arrow = "▲";
                    arrowColor = "#c8ffc8";
                  } else if (iss.diff < -0.005) {
                    arrow = "▼";
                    arrowColor = "#ffc8c8";
                  } else {
                    arrow = "–";
                  }
                }

                return (
                  <Tooltip
                    key={iss.key}
                    title={
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {ISSUE_NAMES[iss.key]}, {(iss.val * 100).toFixed(0)}%
                        </Typography>
                        {iss.diff !== null && (
                          <Typography variant="body2" color="text.secondary">
                            {iss.diff > 0 ? "+" : ""}
                            {(iss.diff * 100).toFixed(1)}% since last wave
                          </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary">
                          {chosenDemog}: {demog}
                        </Typography>
                      </Box>
                    }
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: "white",
                          color: "black",
                          boxShadow: 3,
                          border: "1px solid #ddd",
                        },
                      },
                      arrow: { sx: { color: "white" } },
                    }}
                  >
                    <Box
                      sx={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: ISSUE_COLOURS[iss.key] || "grey",
                        color: "white",
                        fontWeight: "bold",
                        borderRadius: 0,
                        fontSize: 20,
                        gap: 1,
                      }}
                    >
                      {ISSUE_NAMES[iss.key]}
                      {arrow && (
                        <span style={{ color: arrowColor, fontSize: 16 }}>
                          {arrow}
                        </span>
                      )}
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}